/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { StyledInputLabel } from 'components/widgets'
import { PRIMARY_COLOR } from 'lib/constants'
import type { Device, Organization } from 'lib/types'
import { addEssentialOnly, getVoiceButtons, removeEssentialOnly } from 'models/api/alertSources'

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 520,
})

// Matches the Voice Settings section headers it sits beside.
const DenseHeader = styled.div({
  fontFamily: 'Montserrat',
  fontWeight: 700,
  fontSize: '1.0rem',
  color: '#242424',
})

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 24,
})

const InfoTrigger = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  cursor: 'pointer',
  transform: 'translateY(-1px)',
  color: PRIMARY_COLOR,
  opacity: 0.8,
})

const ActionButton = styled(Button)({
  color: PRIMARY_COLOR,
  padding: '6px 16px',
  border: `2px solid ${PRIMARY_COLOR}`,
  boxSizing: 'border-box',
  fontFamily: 'Montserrat',
  fontSize: '0.95rem',
  fontWeight: 'bold',
  lineHeight: '1.2rem',
  letterSpacing: '0.08rem',
  marginRight: 6,
  borderRadius: 4,
  '&.Mui-disabled': { opacity: 0.5, boxShadow: 'none' },
})

// Fits the Add button (MUI's 64px min width, padding and letter spacing).
const ACTION_COL_WIDTH = 88

const cellStyle: React.CSSProperties = {
  fontFamily: 'Montserrat',
  fontSize: 12,
  padding: '5px 12px',
}

const tooltipText = 'Nexus devices with voice features disabled'

const deviceLabel = (device: Device) => device.name || device.id

// A voice button's id is its IMEI.
const DeviceCell = ({ device }: { device: Device }) => (
  <>
    <div>{device.name || '—'}</div>
    <div style={{ color: '#888', fontSize: 11 }}>IMEI: {device.id}</div>
  </>
)

type Props = {
  org: Organization | null
  // Lists the buttons without add/remove controls. Settings shows this view to
  // everyone; super admins change the list from the Admin org page.
  readOnly?: boolean
  // Smaller header and no top margin, for placement beside Voice Settings tables.
  dense?: boolean
}

export const EssentialOnlyButtonsSection = ({ org, readOnly = false, dense = false }: Props) => {
  const orgId = org?.id

  const [voiceButtons, setVoiceButtons] = useState<Device[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')

  const [addId, setAddId] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [actionError, setActionError] = useState('')
  const [removingId, setRemovingId] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!orgId) return
    setLoading(true)
    setLoadError('')
    getVoiceButtons(orgId)
      .then(setVoiceButtons)
      .catch(e => setLoadError(e?.message || 'Unable to load voice buttons.'))
      .finally(() => setLoading(false))
  }, [orgId])

  useEffect(() => {
    load()
  }, [load])

  const excluded = useMemo(
    () => voiceButtons.filter(d => d.essentialOnly).sort((a, b) => deviceLabel(a).localeCompare(deviceLabel(b))),
    [voiceButtons],
  )

  const eligible = useMemo(
    () => voiceButtons.filter(d => !d.essentialOnly).sort((a, b) => deviceLabel(a).localeCompare(deviceLabel(b))),
    [voiceButtons],
  )

  const handleAdd = async () => {
    if (!orgId || !addId) return
    setAddLoading(true)
    setActionError('')
    try {
      await addEssentialOnly(orgId, addId)
      setAddId('')
      load()
    } catch (e: any) {
      setActionError(e?.response?.data?.errors?.[0]?.title || e?.message || 'Unable to add button.')
    } finally {
      setAddLoading(false)
    }
  }

  const handleRemove = async (alertSourceId: string) => {
    if (!orgId || removingId) return
    setRemovingId(alertSourceId)
    setActionError('')
    try {
      await removeEssentialOnly(orgId, alertSourceId)
      load()
    } catch (e: any) {
      setActionError(e?.response?.data?.errors?.[0]?.title || e?.message || 'Unable to remove button.')
    } finally {
      setRemovingId(null)
    }
  }

  if (!org) return null

  return (
    <Wrapper style={{ marginTop: dense ? 0 : 20 }}>
      <HeaderRow style={dense ? { marginBottom: 6 } : undefined}>
        {dense ? (
          <DenseHeader>Essential-only buttons</DenseHeader>
        ) : (
          <StyledInputLabel>Essential-only buttons</StyledInputLabel>
        )}
        <Tooltip title={<span>{tooltipText}</span>} placement="right" arrow>
          <InfoTrigger role="button" tabIndex={0} aria-label="Essential-only buttons info">
            <InfoOutlinedIcon fontSize="small" style={{ display: 'block', color: 'inherit', width: '20px' }} />
          </InfoTrigger>
        </Tooltip>
      </HeaderRow>

      <Paper elevation={1} style={{ maxWidth: 400, maxHeight: 360, overflowY: 'auto', marginTop: dense ? 0 : 8 }}>
        {/* Fixed layout with a reserved action column: under auto layout a long
            selected button name widened the first column past the Paper, which
            clips horizontally (overflowY auto), cutting off the Add button. */}
        <Table size="small" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col />
            {!readOnly && <col style={{ width: ACTION_COL_WIDTH }} />}
          </colgroup>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={readOnly ? 1 : 2} style={{ ...cellStyle, color: '#888', fontStyle: 'italic' }}>
                  Loading…
                </TableCell>
              </TableRow>
            )}

            {!loading && excluded.length === 0 && (
              <TableRow>
                <TableCell colSpan={readOnly ? 1 : 2} style={{ ...cellStyle, color: '#888', fontStyle: 'italic' }}>
                  No essential-only buttons
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              excluded.map(device => (
                <TableRow key={device.id} hover>
                  <TableCell style={{ ...cellStyle, wordBreak: 'break-all' }}>
                    <DeviceCell device={device} />
                  </TableCell>
                  {!readOnly && (
                    <TableCell padding="none" align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(device.id)}
                        disabled={!!removingId}
                        aria-label={`Restore voice for ${deviceLabel(device)}`}
                      >
                        <DeleteIcon style={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}

            {!readOnly && (
              <TableRow>
                <TableCell style={{ ...cellStyle, borderTop: '1px solid rgba(0,0,0,0.08)', paddingRight: 4 }}>
                  <Select
                    value={addId}
                    onChange={e => {
                      setAddId(e.target.value as string)
                      setActionError('')
                    }}
                    displayEmpty
                    disableUnderline
                    style={{ fontFamily: 'Montserrat', fontSize: 12, width: '100%', textTransform: 'none' }}
                  >
                    <MenuItem value="" disabled style={{ fontFamily: 'Montserrat', fontSize: 12 }}>
                      <em style={{ textTransform: 'none', fontStyle: 'italic' }}>
                        {eligible.length === 0 ? 'No voice buttons available' : 'Select a button to add…'}
                      </em>
                    </MenuItem>
                    {eligible.map(device => (
                      <MenuItem
                        key={device.id}
                        value={device.id}
                        style={{ fontFamily: 'Montserrat', fontSize: 12, textTransform: 'none' }}
                      >
                        {device.name ? `${device.name} (IMEI: ${device.id})` : `IMEI: ${device.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell padding="none" align="center" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                  <ActionButton
                    variant="outlined"
                    size="small"
                    onClick={handleAdd}
                    disabled={addLoading || !addId}
                    style={{ padding: '3px 10px', fontSize: '0.85rem', marginTop: 0, marginRight: 0 }}
                  >
                    {addLoading ? '…' : 'Add'}
                  </ActionButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {(actionError || loadError) && (
        <div style={{ color: 'red', marginTop: 8, fontSize: 12 }}>{actionError || loadError}</div>
      )}
    </Wrapper>
  )
}
